// UNIVERSAL-CONTENT-DISPLAY — the one display boundary for stored content.
//
// Server-renderable for every kind; sanitizes/escapes internally exactly
// once. Only custom-element registration is a client enhancement, and it
// loads only when the document actually contains a supported marker.
// Surfaces own layout, skins, and empty visuals; this package owns kind
// resolution, safe rendering, frames, the enhancement boundary, and the
// unified LOCKED look (the synthetic 'locked' kind surfaces render in place
// of withheld content).

import type { CSSProperties, ReactNode } from 'react'
import { sanitizeRichContentHtml } from '../serialize/richHtml'
import { parseContactsDocument, renderContactsHtml } from '../serialize/contacts'
import { parseLockedNotice, renderLockedNoticeHtml } from '../serialize/lockedNotice'
import { RichContentEnhancer } from '../RichContentEnhancer'
import { isCustomHtmlDocument, parseCustomHtmlDocument } from '../serialize/customHtml'
import { CustomHtmlFrame } from './CustomHtmlFrame'
import {
  contentFrameStyle,
  resolveContentDocument,
  RICH_ELEMENT_MARKER_RE,
  type ContentDocument,
  type ContentFrameSpec,
} from './contentDocument'

export interface ContentRendererProps {
  doc: ContentDocument
  className?: string
}

export type ContentRenderer = (props: ContentRendererProps) => ReactNode

// The html renderer preserves the exact DOM the original RichContent
// emitted (data-rich-content="1", className placement) — RichContent is now
// a thin wrapper over it, so existing consumers see zero markup change.
function HtmlContent({ doc, className }: ContentRendererProps) {
  if (isCustomHtmlDocument(doc.value)) return <CustomHtmlContent doc={doc} className={className} />
  const safeHtml = sanitizeRichContentHtml(doc.value)
  const needsEnhancement = RICH_ELEMENT_MARKER_RE.test(safeHtml)
  return (
    <>
      <div className={className} data-rich-content="1" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      {needsEnhancement ? <RichContentEnhancer /> : null}
    </>
  )
}

function CustomHtmlContent({ doc, className }: ContentRendererProps) {
  return <CustomHtmlFrame html={parseCustomHtmlDocument(doc.value) ?? ''} className={className} />
}

// The one canonical plain-text rendering: escaped by React, line breaks
// preserved by CSS, stored bytes untouched. Replaces the per-surface
// `<br/>` injection and the bare-text-node variant that lost newlines.
function PlainContent({ doc, className }: ContentRendererProps) {
  return (
    <div
      className={className}
      data-rich-content="plain"
      style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
    >
      {doc.value}
    </div>
  )
}

function CodeContent({ doc, className }: ContentRendererProps) {
  return (
    <pre
      className={className}
      data-rich-content="code"
      style={{ whiteSpace: 'pre', overflowX: 'auto' }}
    >
      <code>{doc.value}</code>
    </pre>
  )
}

// Contacts is a pure-data kind: the stored envelope's JSON renders through
// the ONE strongly templated string builder (every field escaped there); a
// malformed envelope degrades to the sanitized digest via the html renderer.
function ContactsContent({ doc, className }: ContentRendererProps) {
  const parsed = parseContactsDocument(doc.value)
  if (!parsed) return <HtmlContent doc={{ kind: 'html', value: doc.value }} className={className} />
  return (
    <div
      className={className}
      data-rich-content="contacts"
      dangerouslySetInnerHTML={{ __html: renderContactsHtml(parsed) }}
    />
  )
}

// Locked is the synthetic access-blocked kind (LOCKED-SCREEN): the document
// is built from the access grant, never from stored bytes. The template
// builder escapes every field, so it renders via direct innerHTML like
// contacts; a malformed document renders nothing (it is not user content).
function LockedContent({ doc, className }: ContentRendererProps) {
  const parsed = parseLockedNotice(doc.value)
  if (!parsed) return null
  return (
    <div
      className={className}
      data-rich-content="locked"
      dangerouslySetInnerHTML={{ __html: renderLockedNoticeHtml(parsed) }}
    />
  )
}

const renderers: Record<string, ContentRenderer> = {
  html: HtmlContent,
  plain: PlainContent,
  code: CodeContent,
  contacts: ContactsContent,
  'custom-html': CustomHtmlContent,
  locked: LockedContent,
}

/** Register a renderer for a new content kind (allowlisted: unknown kinds
 * never reach raw output — they fall back to the escaped plain renderer). */
export function registerContentRenderer(kind: string, renderer: ContentRenderer): void {
  renderers[kind] = renderer
}

export interface ContentViewProps {
  /** Pre-resolved document; or supply the raw fields below. */
  doc?: ContentDocument | null
  bodyHtml?: string | null
  body?: string | null
  className?: string
  frame?: ContentFrameSpec
}

export function ContentView({ doc, bodyHtml, body, className, frame }: ContentViewProps) {
  const resolved = doc !== undefined ? doc : resolveContentDocument({ bodyHtml, body })
  if (!resolved || !resolved.value) return null

  const Renderer = renderers[resolved.kind] ?? PlainContent
  const content = <Renderer doc={resolved} className={className} />

  const frameStyle = contentFrameStyle(frame)
  if (!frameStyle) return content
  return (
    <div data-content-frame={frame!.kind} style={frameStyle as CSSProperties}>
      {content}
    </div>
  )
}
